import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ConfirmModal from '../../../../components/modals/utils/ConfirmModal';
import COLORS from '../../../../resources/style/Themes';

export default function EditFirmwareVersioningModal({
  show,
  onHide,
  versioning,
  saveChanges,
}) {
  const [latestAppVersionAndroid, setLatestAppVersionAndroid] = useState('');
  const [latestAppVersioniOS, setLatestAppVersioniOS] = useState('');
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState('');
  const [minimumAppVersionAndroid, setMinimumAppVersionAndroid] = useState('');
  const [minimumAppVersioniOS, setMinimumAppVersioniOS] = useState('');
  const [
    currentManufacturingFirmwareVersion,
    setCurrentManufacturingFirmwareVersion,
  ] = useState('');
  const [initialValues, setInitialValues] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!versioning || !show) {
      return;
    }

    const latestAndroid = versioning.latest_app_version_android ?? '';
    const latestIos = versioning.latest_app_version_ios ?? '';
    const latestFirmware = versioning.latest_firmware_version ?? '';
    const minimumAndroid = versioning.minimum_app_version_android ?? '';
    const minimumIos = versioning.minimum_app_version_ios ?? '';
    const currentManufacturing =
      versioning.current_manufacturing_firmware_version ?? '';

    setLatestAppVersionAndroid(latestAndroid);
    setLatestAppVersioniOS(latestIos);
    setLatestFirmwareVersion(latestFirmware);
    setMinimumAppVersionAndroid(minimumAndroid);
    setMinimumAppVersioniOS(minimumIos);
    setCurrentManufacturingFirmwareVersion(currentManufacturing);
    setInitialValues({
      latest_app_version_android: latestAndroid,
      latest_app_version_ios: latestIos,
      latest_firmware_version: latestFirmware,
      minimum_app_version_android: minimumAndroid,
      minimum_app_version_ios: minimumIos,
      current_manufacturing_firmware_version: currentManufacturing,
    });
  }, [versioning, show]);

  const hasChanges =
    !!initialValues &&
    (initialValues.latest_app_version_android !== latestAppVersionAndroid ||
      initialValues.latest_app_version_ios !== latestAppVersioniOS ||
      initialValues.latest_firmware_version !== latestFirmwareVersion ||
      initialValues.minimum_app_version_android !== minimumAppVersionAndroid ||
      initialValues.minimum_app_version_ios !== minimumAppVersioniOS ||
      initialValues.current_manufacturing_firmware_version !==
        currentManufacturingFirmwareVersion);

  const handleSave = () => {
    setShowConfirmModal(false);
    const jsonObject = {
      latest_app_version_android: latestAppVersionAndroid,
      latest_app_version_ios: latestAppVersioniOS,
      latest_firmware_version: latestFirmwareVersion,
      minimum_app_version_android: minimumAppVersionAndroid,
      minimum_app_version_ios: minimumAppVersioniOS,
      current_manufacturing_firmware_version:
        currentManufacturingFirmwareVersion,
    };
    saveChanges(jsonObject);
  };

  return (
    <>
      <Dialog open={show} onClose={onHide} fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: COLORS.SAFEGUARD_BLUE,
            color: COLORS.WHITE,
            borderBottom: 'none',
            py: 2,
          }}
        >
          Edit Versioning
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 0.5 }}>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              mt: 0.5,
              pt: 3,
              pb: 2,
            }}
          >
            <TextField
              label="Latest App Version Android"
              variant="outlined"
              fullWidth
              value={latestAppVersionAndroid}
              onChange={(e) => setLatestAppVersionAndroid(e.target.value)}
            />
            <TextField
              label="Latest App Version iOS"
              variant="outlined"
              fullWidth
              value={latestAppVersioniOS}
              onChange={(e) => setLatestAppVersioniOS(e.target.value)}
            />
            <TextField
              label="Latest Firmware Version"
              variant="outlined"
              fullWidth
              value={latestFirmwareVersion}
              onChange={(e) => setLatestFirmwareVersion(e.target.value)}
            />
            <TextField
              label="Minimum App Version Android"
              variant="outlined"
              fullWidth
              value={minimumAppVersionAndroid}
              onChange={(e) => setMinimumAppVersionAndroid(e.target.value)}
            />
            <TextField
              label="Minimum App Version iOS"
              variant="outlined"
              fullWidth
              value={minimumAppVersioniOS}
              onChange={(e) => setMinimumAppVersioniOS(e.target.value)}
            />
            <TextField
              label="Current Manufacturing Firmware Version"
              variant="outlined"
              fullWidth
              value={currentManufacturingFirmwareVersion}
              onChange={(e) =>
                setCurrentManufacturingFirmwareVersion(e.target.value)
              }
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setShowConfirmModal(false);
              onHide();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!hasChanges}
            onClick={() => {
              setConfirmMessage(
                'Are you sure you want to modify App versioning? This will immediately impact user-facing mobile applications. Be safe and double-check your values. If you are not sure of what you are doing, stop!',
              );
              setConfirmTitle('Modify App Versioning');
              setShowConfirmModal(true);
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmModal
        show={showConfirmModal}
        confirm={() => {
          onHide();
          handleSave();
        }}
        onHide={() => {
          setShowConfirmModal(false);
        }}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />
    </>
  );
}
